import {
  Grid,
  Box,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Button,
  FormControlLabel,
  Checkbox,
  Paper,
  Typography,
} from '@mui/material'
import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react'
import { fontSizes } from '../../../../Common/Typography/TypographyStyles'
import { gradientColors, textColors, notificationColors } from '../../../../Common/Color/Color'
import ChipInputField from './ChipInputField'

interface TaskAssignmentProps {
  handleChange: (
    eventOrValue:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | Date
      | string[]
      | boolean
      | null,
    fieldName?: string,
  ) => void
  formData: {
    operationalManager: string | string[]
    functionalArea: string | string[]
    supervisor: string | string[]
    required: boolean
  }
  error: {
    operationalManager: string
    functionalArea: string
    supervisor: string
  }
  teamMembers?: any[]
}

export interface TaskAssignmentRef {
  validateAssignments: () => boolean
}

const TaskAssignment = forwardRef<TaskAssignmentRef, TaskAssignmentProps>(
  ({ handleChange, formData, error, teamMembers = [] }, ref) => {
    const [dialogOpen, setDialogOpen] = useState(false)
    const [incompatibleItems, setIncompatibleItems] = useState<{
      field: string
      items: string[]
    } | null>(null)

    const handleAddChip = (value: string, fieldName: string) => {
      const currentValues = ensureArray(formData[fieldName as keyof Omit<typeof formData, 'required'>])

      if (value === 'ALL_OPERATIONAL_MANAGERS') {
        const allManagerIds = availableOperationalManagers.map((m) => m.workdayId)
        handleChange(['ALL_OPERATIONAL_MANAGERS', ...allManagerIds], fieldName)
        return
      }
      if (value === 'ALL_FUNCTIONAL_AREAS') {
        const allAreaValues = availableFunctionalAreas.map((a) => a.value)
        handleChange(['ALL_FUNCTIONAL_AREAS', ...allAreaValues], fieldName)
        return
      }
      if (value === 'ALL_SUPERVISORS') {
        const allSupervisorIds = availableSupervisors.map((s) => s.workdayId)
        handleChange(['ALL_SUPERVISORS', ...allSupervisorIds], fieldName)
        return
      }

      if (!currentValues.includes(value)) {
        const newValues = [...currentValues, value]
        // Auto-select "All" if all individual items are now selected
        if (fieldName === 'operationalManager') {
          const allIds = availableOperationalManagers.map((m) => m.workdayId)
          const allSelected = allIds.every((id) => newValues.includes(id))
          if (allSelected && allIds.length > 1) {
            handleChange(['ALL_OPERATIONAL_MANAGERS', ...allIds], fieldName)
            return
          }
        }
        if (fieldName === 'functionalArea') {
          const allVals = availableFunctionalAreas.map((a) => a.value)
          const allSelected = allVals.every((v) => newValues.includes(v))
          if (allSelected && allVals.length > 1) {
            handleChange(['ALL_FUNCTIONAL_AREAS', ...allVals], fieldName)
            return
          }
        }
        if (fieldName === 'supervisor') {
          const allIds = availableSupervisors.map((s) => s.workdayId)
          const allSelected = allIds.every((id) => newValues.includes(id))
          if (allSelected && allIds.length > 1) {
            handleChange(['ALL_SUPERVISORS', ...allIds], fieldName)
            return
          }
        }
        handleChange(newValues, fieldName)
      }
    }

    const handleRemoveChip = (valueToRemove: string, fieldName: string) => {
      const currentValues = ensureArray(formData[fieldName as keyof Omit<typeof formData, 'required'>])
      const currValOperationalManager = ensureArray(
        formData['operationalManager' as keyof Omit<typeof formData, 'required'>],
      )
      const currValFunctionalArea = ensureArray(
        formData['functionalArea' as keyof Omit<typeof formData, 'required'>],
      )
      const currValSupervisor = ensureArray(
        formData['supervisor' as keyof Omit<typeof formData, 'required'>],
      )

      const newValues = currentValues.filter((value) => value !== valueToRemove)
      handleChange(newValues, fieldName)

      // Special handling for "All" options
      if (
        fieldName !== 'operationalManager' &&
        currValOperationalManager.includes('ALL_OPERATIONAL_MANAGERS')
      ) {
        const newValOperationalManager = currValOperationalManager.filter(
          (value) => value !== 'ALL_OPERATIONAL_MANAGERS',
        )
        handleChange(newValOperationalManager, 'operationalManager')
      }

      if (fieldName !== 'functionalArea' && currValFunctionalArea.includes('ALL_FUNCTIONAL_AREAS')) {
        const newValFunctionalArea = currValFunctionalArea.filter(
          (value) => value !== 'ALL_FUNCTIONAL_AREAS',
        )
        handleChange(newValFunctionalArea, 'functionalArea')
      }

      if (fieldName !== 'supervisor' && currValSupervisor.includes('ALL_SUPERVISORS')) {
        const newValSupervisor = currValSupervisor.filter((value) => value !== 'ALL_SUPERVISORS')
        handleChange(newValSupervisor, 'supervisor')
      }
    }

    const handleRequiredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked
      handleChange(value, 'required')
    }

    // Helper function to get name from workday ID for any field type
    const getNameFromId = (id: string, fieldType: 'operationalManager' | 'supervisor'): string => {
      const member = teamMembers.find((member) => {
        if (fieldType === 'operationalManager') {
          return member.operationalManager && member.operationalManager.workdayId === id
        } else if (fieldType === 'supervisor') {
          return member.supervisor && member.supervisor.workdayId === id
        }
        return false
      })

      if (fieldType === 'operationalManager') {
        return member?.operationalManager?.name || id
      } else if (fieldType === 'supervisor') {
        return member?.supervisor?.name || id
      }
      return id
    }

    // Handle removing incompatible items
    const handleRemoveIncompatibleItems = () => {
      if (!incompatibleItems) return

      const { field } = incompatibleItems

      if (field === 'operationalManager') {
        const selectedOperationalManagers = ensureArray(formData.operationalManager)
        const availableOperationalManagerIds = availableOperationalManagers.map((om) => om.workdayId)
        const compatibleItems = selectedOperationalManagers.filter((id) =>
          availableOperationalManagerIds.includes(id),
        )
        handleChange(compatibleItems, 'operationalManager')
      } else if (field === 'functionalArea') {
        const selectedFunctionalAreas = ensureArray(formData.functionalArea)
        const availableFunctionalAreaValues = availableFunctionalAreas.map((fa) => fa.value)
        const compatibleItems = selectedFunctionalAreas.filter((area) =>
          availableFunctionalAreaValues.includes(area),
        )
        handleChange(compatibleItems, 'functionalArea')
      } else if (field === 'supervisor') {
        const selectedSupervisors = ensureArray(formData.supervisor)
        const availableSupervisorIds = availableSupervisors.map((s) => s.workdayId)
        const compatibleItems = selectedSupervisors.filter((id) => availableSupervisorIds.includes(id))
        handleChange(compatibleItems, 'supervisor')
      }

      setDialogOpen(false)
      setIncompatibleItems(null)
    }

    const handleKeepIncompatibleItems = () => {
      setDialogOpen(false)
      setIncompatibleItems(null)
    }

    // Helper function to ensure values are arrays and filter out empty strings
    const ensureArray = (value: string | string[]): string[] => {
      if (Array.isArray(value)) {
        return value.filter((item) => item && item.trim() !== '') // Filter out empty strings and whitespace
      }
      return value && value.trim() !== '' ? [value] : []
    }

    // Bidirectional filtering - operational managers filtered by functional area and supervisor
    const getAvailableOperationalManagers = () => {
      const selectedFunctionalAreas = ensureArray(formData.functionalArea)
      const selectedSupervisors = ensureArray(formData.supervisor)

      let filteredMembers = teamMembers.filter(
        (member) => member.status === 'active' && member.operationalManager && member.operationalManager.workdayId,
      )

      // Filter by functional areas if selected (exclude ALL_ options from filtering)
      if (selectedFunctionalAreas.length > 0) {
        const actualSelectedAreas = selectedFunctionalAreas.filter(
          (area) => area !== 'ALL_FUNCTIONAL_AREAS',
        )
        if (actualSelectedAreas.length > 0) {
          filteredMembers = filteredMembers.filter(
            (member) => member.functionalArea && actualSelectedAreas.includes(member.functionalArea),
          )
        }
      }

      // Filter by supervisors if selected (exclude ALL_ options from filtering)
      if (selectedSupervisors.length > 0) {
        const actualSelectedSupervisors = selectedSupervisors.filter((id) => id !== 'ALL_SUPERVISORS')
        if (actualSelectedSupervisors.length > 0) {
          filteredMembers = filteredMembers.filter(
            (member) =>
              member.supervisor && actualSelectedSupervisors.includes(member.supervisor.workdayId),
          )
        }
      }

      // Extract unique operational managers and sort by name
      const uniqueOperationalManagers = Array.from(
        new Set(filteredMembers.map((member) => member.operationalManager.workdayId)),
      )
        .map((workdayId) => {
          const manager = filteredMembers.find(
            (member) => member.operationalManager && member.operationalManager.workdayId === workdayId,
          )?.operationalManager
          return manager ? { workdayId, name: manager.name } : null
        })
        .filter((manager): manager is { workdayId: string; name: string } => manager !== null)
        .sort((a, b) => a.name.localeCompare(b.name))

      return uniqueOperationalManagers
    }

    const getAvailableFunctionalAreas = () => {
      const selectedOperationalManagers = ensureArray(formData.operationalManager)
      const selectedSupervisors = ensureArray(formData.supervisor)

      let filteredMembers = teamMembers.filter((member) => member.status === 'active' && member.functionalArea)

      // Filter by operational managers if selected (exclude ALL_ options from filtering)
      if (selectedOperationalManagers.length > 0) {
        const actualSelectedManagers = selectedOperationalManagers.filter(
          (id) => id !== 'ALL_OPERATIONAL_MANAGERS',
        )
        if (actualSelectedManagers.length > 0) {
          filteredMembers = filteredMembers.filter(
            (member) =>
              member.operationalManager &&
              actualSelectedManagers.includes(member.operationalManager.workdayId),
          )
        }
      }

      // Filter by supervisors if selected (exclude ALL_ options from filtering)
      if (selectedSupervisors.length > 0) {
        const actualSelectedSupervisors = selectedSupervisors.filter((id) => id !== 'ALL_SUPERVISORS')
        if (actualSelectedSupervisors.length > 0) {
          filteredMembers = filteredMembers.filter(
            (member) =>
              member.supervisor && actualSelectedSupervisors.includes(member.supervisor.workdayId),
          )
        }
      }

      // Extract functional areas and sort
      const functionalAreas = filteredMembers
        .map((member) => member.functionalArea)
        .filter((area) => area) // Remove any null/undefined values

      // Remove duplicates and sort
      return Array.from(new Set(functionalAreas))
        .sort()
        .map((area) => ({ value: area }))
    }

    const getAvailableSupervisors = () => {
      const selectedOperationalManagers = ensureArray(formData.operationalManager)
      const selectedFunctionalAreas = ensureArray(formData.functionalArea)

      let filteredMembers = teamMembers.filter(
        (member) => member.status === 'active' && member.supervisor && member.supervisor.workdayId,
      )

      // Filter by operational managers if selected (exclude ALL_ options from filtering)
      if (selectedOperationalManagers.length > 0) {
        const actualSelectedManagers = selectedOperationalManagers.filter(
          (id) => id !== 'ALL_OPERATIONAL_MANAGERS',
        )
        if (actualSelectedManagers.length > 0) {
          filteredMembers = filteredMembers.filter(
            (member) =>
              member.operationalManager &&
              actualSelectedManagers.includes(member.operationalManager.workdayId),
          )
        }
      }

      // Filter by functional areas if selected (exclude ALL_ options from filtering)
      if (selectedFunctionalAreas.length > 0) {
        const actualSelectedAreas = selectedFunctionalAreas.filter(
          (area) => area !== 'ALL_FUNCTIONAL_AREAS',
        )
        if (actualSelectedAreas.length > 0) {
          filteredMembers = filteredMembers.filter(
            (member) => member.functionalArea && actualSelectedAreas.includes(member.functionalArea),
          )
        }
      }

      // Extract unique supervisors and sort by name
      const uniqueSupervisors = Array.from(
        new Set(filteredMembers.map((member) => member.supervisor.workdayId)),
      )
        .map((workdayId) => {
          const supervisor = filteredMembers.find(
            (member) => member.supervisor && member.supervisor.workdayId === workdayId,
          )?.supervisor
          return supervisor ? { workdayId, name: supervisor.name } : null
        })
        .filter((supervisor): supervisor is { workdayId: string; name: string } => supervisor !== null)
        .sort((a, b) => a.name.localeCompare(b.name))

      return uniqueSupervisors
    }

    const availableOperationalManagers = getAvailableOperationalManagers()
    const availableFunctionalAreas = getAvailableFunctionalAreas()
    const availableSupervisors = getAvailableSupervisors()

    // Auto-select when only 1 option is available
    useEffect(() => {
      const currentOperationalManagers = ensureArray(formData.operationalManager)
      if (
        availableOperationalManagers.length === 1 &&
        (currentOperationalManagers.length === 0 ||
          (currentOperationalManagers.length === 1 &&
            currentOperationalManagers[0] !== availableOperationalManagers[0].workdayId))
      ) {
        handleChange([availableOperationalManagers[0].workdayId], 'operationalManager')
      }
    }, [availableOperationalManagers.length])

    useEffect(() => {
      const currentFunctionalAreas = ensureArray(formData.functionalArea)
      if (
        availableFunctionalAreas.length === 1 &&
        (currentFunctionalAreas.length === 0 ||
          (currentFunctionalAreas.length === 1 &&
            currentFunctionalAreas[0] !== availableFunctionalAreas[0].value))
      ) {
        handleChange([availableFunctionalAreas[0].value], 'functionalArea')
      }
    }, [availableFunctionalAreas.length])

    useEffect(() => {
      const currentSupervisors = ensureArray(formData.supervisor)
      if (
        availableSupervisors.length === 1 &&
        (currentSupervisors.length === 0 ||
          (currentSupervisors.length === 1 &&
            currentSupervisors[0] !== availableSupervisors[0].workdayId))
      ) {
        handleChange([availableSupervisors[0].workdayId], 'supervisor')
      }
    }, [availableSupervisors.length])

    // Validation function to check for incompatibilities before form submission
    const validateAssignments = (): boolean => {
      const selectedOperationalManagers = ensureArray(formData.operationalManager)
      const selectedFunctionalAreas = ensureArray(formData.functionalArea)
      const selectedSupervisors = ensureArray(formData.supervisor)

      // If no selections, validation passes
      if (
        selectedOperationalManagers.length === 0 &&
        selectedFunctionalAreas.length === 0 &&
        selectedSupervisors.length === 0
      ) {
        return true
      }

      const availableOperationalManagerIds = availableOperationalManagers.map((om) => om.workdayId)
      const availableFunctionalAreaValues = availableFunctionalAreas.map((fa) => fa.value)
      const availableSupervisorIds = availableSupervisors.map((s) => s.workdayId)

      // Check for incompatible selections
      const incompatibleOperationalManagers = selectedOperationalManagers.filter(
        (id) => id !== 'ALL_OPERATIONAL_MANAGERS' && !availableOperationalManagerIds.includes(id),
      )
      const incompatibleFunctionalAreas = selectedFunctionalAreas.filter(
        (area) => area !== 'ALL_FUNCTIONAL_AREAS' && !availableFunctionalAreaValues.includes(area),
      )
      const incompatibleSupervisors = selectedSupervisors.filter(
        (id) => id !== 'ALL_SUPERVISORS' && !availableSupervisorIds.includes(id),
      )

      // If any incompatibilities found, set the items and show main dialog
      if (incompatibleOperationalManagers.length > 0) {
        const names = incompatibleOperationalManagers.map((id) =>
          getNameFromId(id, 'operationalManager'),
        )
        setIncompatibleItems({
          field: 'operationalManager',
          items: names,
        })
        setDialogOpen(true)
        return false
      } else if (incompatibleFunctionalAreas.length > 0) {
        setIncompatibleItems({
          field: 'functionalArea',
          items: incompatibleFunctionalAreas,
        })
        setDialogOpen(true)
        return false
      } else if (incompatibleSupervisors.length > 0) {
        const names = incompatibleSupervisors.map((id) => getNameFromId(id, 'supervisor'))
        setIncompatibleItems({
          field: 'supervisor',
          items: names,
        })
        setDialogOpen(true)
        return false
      }

      return true // No incompatibilities found
    }

    // Expose validation function to parent component
    useImperativeHandle(ref, () => ({
      validateAssignments,
    }))

    return (
      <Grid container spacing={2} direction='column'>
        {/* Documentation Required Checkbox */}
        <Grid>
          <Box sx={{ marginTop: 2 }}>
            <FormControlLabel
              control={
                <Checkbox checked={formData.required} onChange={handleRequiredChange} name='required' />
              }
              label='Documentation Required'
            />
          </Box>
        </Grid>

        <Grid>
          <ChipInputField
            label='Operations/Practice Manager'
            required
            error={error.operationalManager}
            options={availableOperationalManagers.map((m) => ({ value: m.workdayId, label: m.name }))}
            selectedValues={ensureArray(formData.operationalManager).filter((v) => v !== 'ALL_OPERATIONAL_MANAGERS')}
            onAdd={(value) => handleAddChip(value, 'operationalManager')}
            onRemove={(value) => handleRemoveChip(value, 'operationalManager')}
            onClear={() => handleChange([], 'operationalManager')}
            getChipLabel={(value) => {
              if (value === 'ALL_OPERATIONAL_MANAGERS') return 'All Operations/Practice Manager'
              const m = availableOperationalManagers.find((om) => om.workdayId === value)
              return m ? m.name : getNameFromId(value, 'operationalManager')
            }}
            showAllOption={availableOperationalManagers.length > 1}
            allOptionValue='ALL_OPERATIONAL_MANAGERS'
            allOptionLabel='All Operations/Practice Manager'
          />
        </Grid>
        <Grid>
          <ChipInputField
            label='Functional Area'
            required
            error={error.functionalArea}
            options={availableFunctionalAreas.map((a) => ({ value: a.value, label: a.value }))}
            selectedValues={ensureArray(formData.functionalArea).filter((v) => v !== 'ALL_FUNCTIONAL_AREAS')}
            onAdd={(value) => handleAddChip(value, 'functionalArea')}
            onRemove={(value) => handleRemoveChip(value, 'functionalArea')}
            onClear={() => handleChange([], 'functionalArea')}
            getChipLabel={(value) => value}
            showAllOption={availableFunctionalAreas.length > 1}
            allOptionValue='ALL_FUNCTIONAL_AREAS'
            allOptionLabel='All Functional Area'
          />
        </Grid>
        <Grid>
          <ChipInputField
            label='Supervisor'
            required
            error={error.supervisor}
            options={availableSupervisors.map((s) => ({ value: s.workdayId, label: s.name }))}
            selectedValues={ensureArray(formData.supervisor).filter((v) => v !== 'ALL_SUPERVISORS')}
            onAdd={(value) => handleAddChip(value, 'supervisor')}
            onRemove={(value) => handleRemoveChip(value, 'supervisor')}
            onClear={() => handleChange([], 'supervisor')}
            getChipLabel={(value) => {
              if (value === 'ALL_SUPERVISORS') return 'All Supervisors'
              const s = availableSupervisors.find((sup) => sup.workdayId === value)
              return s ? s.name : getNameFromId(value, 'supervisor')
            }}
            showAllOption={availableSupervisors.length > 1}
            allOptionValue='ALL_SUPERVISORS'
            allOptionLabel='All Supervisors'
          />
        </Grid>

        {/* Incompatibility Confirmation Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleKeepIncompatibleItems}
          aria-labelledby='incompatible-dialog-title'
          aria-describedby='incompatible-dialog-description'
          maxWidth='xs'
        >
          <DialogTitle
            id='incompatible-dialog-title'
            sx={{ fontWeight: 'bold', fontSize: fontSizes.size20 }}
          >
            Incompatible Selection
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Box sx={{ minWidth: 400 }}>
              {incompatibleItems && (
                <>
                  {/* Incompatible Items Highlight */}
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      mb: 3,
                      background: gradientColors.redGradient,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: notificationColors.redDark,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {incompatibleItems.items.map((item, index) => (
                        <Typography
                          key={index}
                          variant='h6'
                          sx={{ fontWeight: 700, color: textColors.white }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Paper>

                  {/* Explanation Text */}
                  <Typography variant='body1' sx={{ fontSize: fontSizes.size14 }}>
                    The highlighted{' '}
                    {incompatibleItems.field === 'operationalManager'
                      ? 'Operations/Practice Manager(s)'
                      : incompatibleItems.field === 'functionalArea'
                        ? 'Functional Area(s)'
                        : 'Supervisor(s)'}{' '}
                    are no longer compatible with your current selections.
                  </Typography>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button color='error' onClick={handleKeepIncompatibleItems}>
              Keep
            </Button>
            <Button onClick={handleRemoveIncompatibleItems} autoFocus>
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    )
  },
)

TaskAssignment.displayName = 'TaskAssignment'

export default TaskAssignment
